import { BookOpenText, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import ConnectorSetup from 'src/components/connectors/ConnectorSetup';
import jsCookie from "js-cookie";
import axios from 'axios';
import { API_PROD_URL } from 'src/utils/axios';
import { useNavigate } from 'react-router-dom';
import { getUserProfile } from 'src/utils/api/userProfile';
import Loading from 'src/components/reusable/loading';
import DotLoader from 'src/components/reusable/dot-loader';

const AllCampaignDashboard = () => {
  const navigate = useNavigate();
  const userId = jsCookie.get("user_id");
  const [user, setUser] = useState<any | null>(null);
  const [userData, setUserData] = useState<any>(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6); // You can adjust this number
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchCampaigns = async (page = 1, limit = itemsPerPage) => {
    setLoading(true);
    try {
      const campaignsResponse = await axios.get(`${API_PROD_URL}/marketing-agent/campaigns`, {
        params: { 
          userId, 
          page: page,
          limit: limit
        },
      });
      
      setUserData(campaignsResponse.data);
      // Assuming the API returns total count in the response
      // If not, you might need to adjust based on your API response structure
      if (campaignsResponse.data.total) {
        setTotalCampaigns(campaignsResponse.data.total);
      } else if (campaignsResponse.data.campaigns) {
        // If total is not provided, estimate based on returned data
        setTotalCampaigns(campaignsResponse.data.campaigns.length);
      }
      
   
    } catch (error) {
      console.error("Error fetching campaigns:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = await getUserProfile();
        setUser(user);
      
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (userId) {
      fetchCampaigns(currentPage, itemsPerPage);
    }
  }, [userId, currentPage, itemsPerPage]);

  // Calculate pagination info
  const totalPages = Math.ceil(totalCampaigns / itemsPerPage);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(currentPage - 1)}
        // disabled={!hasPrevPage || loading}
        // className={`px-3 py-2 rounded-md flex items-center ${
        //   !hasPrevPage || loading
        //     ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
        //     : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
        // }`}
      >
        <ChevronLeft size={16} />
        
      </button>
    );

    // Page number buttons
    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          disabled={loading}
          className={` ${
            currentPage === i
              ? ' text-gray-700'
              : ' text-gray-300 '
          } ${loading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          {i}
        </button>
      );
    }

    // Next button
    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(currentPage + 1)}
        // disabled={!hasNextPage || loading}
        // className={`px-3 py-2 rounded-md flex items-center ${
        //   !hasNextPage || loading
        //     ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
        //     : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
        // }`}
      >
        <ChevronRight size={16} />
      </button>
    );

    return buttons;
  };

  return (
    <div className="min-h-screen w-full py-3">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">
            Welcome {user?.first_name}{user?.last_name},
          </h1>
          <p className="text-gray-600">Your marketing campaigns and connector setup</p>
        </div>

        <div className="flex flex-col md:flex-row">
          {/* Campaigns Grid */}
          <div className="lg:col-span-2 md:w-3/5 mr-4">
            {/* Campaigns Count Info */}
            {/* <div className="mb-4 flex justify-between items-center">
              {totalPages > 1 && (
                <p className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </p>
              )}
            </div> */}

            {/* Loading State */}
             {loading && (
             <div className='flex justify-center mt-15'>
              <DotLoader />
             </div>
            )
            }
            

            {/* Campaigns Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[74vh] overflow-y-auto pn_scroller">
             
              {!loading && userData?.campaigns?.length > 0 ? (
                userData.campaigns.map((campaign: any) => (
                  <div
                    key={campaign.id}
                    onClick={() => navigate(`/marketing-agent/campaign-dashboard/${campaign.id}`)}
                    className="bg-white rounded-lg border border-gray-300 p-3 hover:shadow-sm transition-shadow hover:bg-[#FFF8EC] cursor-pointer"
                  >
                    {/* Header */}
                    <div className="flex items-center mb-2">
                      <BookOpenText />
                      <h3 className="font-medium text-gray-900 ml-1">
                        {campaign?.template?.title}
                      </h3>
                    </div>

                    {/* Main Info */}
                    <div className="space-y-1 text-sm text-gray-700">
                      <div className="flex items-center">
                        <span className="text-blue-600 mr-2">🎯</span>
                        Goal: {campaign?.template?.strategy?.main_goals?.join(", ") || "N/A"}
                      </div>

                      <div className="flex items-center">
                        <span className="text-green-600 mr-2">💰</span>
                        Budget: {campaign?.template?.budgeting?.total_budget || "N/A"}
                      </div>

                      <div className="flex items-center">
                        <span className="text-orange-600 mr-2">📅</span>
                        Created: {new Date(campaign.created_at).toLocaleDateString()}
                      </div>

                      <div className="flex items-center">
                        <span className="text-purple-600 mr-2">📊</span>
                        Attribution: {campaign?.template?.attribution?.models?.join(", ") || "N/A"}
                      </div>

                      <div className="flex items-center">
                        <span className="text-indigo-600 mr-2">🔗</span>
                        Thread ID: {campaign.thread_id}
                      </div>
                    </div>
                  </div>
                ))
              ) : !loading && (
                <div className="col-span-full text-center py-8">
                  <p className="text-gray-600">No campaigns found</p>
                </div>
              )}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && !loading && (
              <div className="mt-2 flex justify-end">
                <div className="flex gap-2">
                  {renderPaginationButtons()}
                </div>
              </div>
            )}
            
          </div>
          

          {/* Connector Setup */}
          <div className="md:w-2/5">
            <ConnectorSetup campaignData={userId} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllCampaignDashboard;